import {Injectable} from '@angular/core';
import {
  HttpErrorResponse,
  HttpHandler,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpParams,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpUserEvent
} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {Router} from '@angular/router';
import {catchError} from 'rxjs/operators';

import {AuthService} from 'app/services/auth.service';
import {DialogHelperService} from './dialog-helper.service';

@Injectable()
export class RequestInterceptorService implements HttpInterceptor {

  constructor(
    private authService: AuthService,
    private router: Router,
    private dialogHelper: DialogHelperService
  ) {}

  addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
    // avoiding IE caching
    let newParams = new HttpParams({ fromString: req.params.toString() });
    newParams = newParams.append('time', Date.now() + '');
    return req.clone({ setHeaders: { Authorization: token }, params: newParams });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<any> | HttpUserEvent<any>> {
    const isAuthUrl = req.url.includes('authUser') || req.url.includes('redirect');
    const hasAuthHeader = req.headers.keys().some((key) => key.toLocaleLowerCase() === 'authorization');
    
    if (!isAuthUrl && !hasAuthHeader) {
      const token = this.authService.getTokenLocal();
      if (token) {
        if (this.authService.isTokenExpired()) {
          // Clear the expired token and let the request through without auth.
          // The backend will decide based on is_public; if auth is truly required
          // (admin routes with Lambda authorizer), API Gateway returns 401 which
          // is caught below and triggers the login redirect.
          this.authService.logout();
          return next.handle(req).pipe(
            catchError(error => {
              if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
                return this.handle401Error(error);
              }
              return throwError(error);
            })
          );
        }

        return next.handle(this.addToken(req, token))
          .pipe(catchError(error => {
            if (error instanceof HttpErrorResponse) {
              if (error.status === 400) {
                return this.handle400Error(error);
              } else if (error.status === 401 || (error.status === 0 && this.authService.isTokenExpired())) {
                return this.handle401Error(error);
              }
            }
            return throwError(error);
          }));
      }
      return next.handle(req);
    } else {
      return next.handle(req);
    }
  }

  handle400Error(error): Observable<never> {
    if (error && (error.status === 400 || error.status === 403) && error.error && error.error.error === 'invalid_grant') {
      this.logoutUser();
    }
    return throwError(error);
  }

  handle401Error(error): Observable<never> {
    this.dialogHelper.showPrompt(
      'Unauthorized error',
      'Your session expired, refreshing your login...',
      'OK',
      () => {}
    );
    error.handled = true;
    this.logoutUser();
    return throwError(error);
  }

  private logoutUser() {
    this.authService.logout();
    this.router.navigate(['login']);
  }
}
